import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import Cookies from "js-cookie";
import loginService from "../../Services/loginService";
import logo from "../../assets/logo.png";
import { MdEmail, MdLock, MdVisibility, MdVisibilityOff, MdErrorOutline, MdArrowForward } from "react-icons/md";
import { AiOutlineLoading3Quarters } from "react-icons/ai";

const Login = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [data, setData] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState("");

  const handleChange = (value, field) => {
    setData((prev) => ({ ...prev, [field]: value }));
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await loginService.login(data);
      toast.success("Login successful!");
      Cookies.set(
        "XIOQUNVU1RPTUVSLUFVVEhFTlRJQ0FUSU9OIMSLQ1JFVC1LRVk=",
        response.token,
        { expires: 1 }
      );
      const user = response.reception;
      localStorage.setItem("user_id", user.id);
      localStorage.setItem("user_name", user.name);
      localStorage.setItem("user_email", user.email);
      navigate("/receptionist");
    } catch (err) {
      toast.error(err.message || "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSubmit(e);
  };

  return (
    <div
      className="min-h-screen flex overflow-hidden"
      style={{ fontFamily: "'DM Sans', sans-serif" }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:wght@300;400;500;600&display=swap');
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .fade-up { animation: fadeUp 1.5s ease both; }
      `}</style>

      {/* ── Left decorative panel ── */}
      <div
        className="hidden md:flex w-[45%] flex-col justify-between p-12 relative overflow-hidden"
        style={{ background: "linear-gradient(155deg, #0a2540 0%, #D88D5D 100%)" }}
      >
        {/* Decorative circles */}
        <div className="absolute -top-28 -right-28 w-[420px] h-[420px] rounded-full bg-white/[0.04] pointer-events-none" />
        <div className="absolute -bottom-20 -left-20 w-[300px] h-[300px] rounded-full bg-white/[0.04] pointer-events-none" />

        {/* Brand */}
        <div className="flex items-center gap-3 relative z-10">
          <img src={logo} alt="Hospital Logo" className="w-10 h-10 object-contain" />
          <span
            className="text-white text-xl tracking-wide"
            style={{ fontFamily: "'DM Serif Display', serif" }}
          >
            Said Ahmed Memorial Hospital
          </span>
        </div>

        {/* Headline */}
        <div className="relative z-10">
          <h1
            className="text-white text-[42px] leading-[1.15] mb-5"
            style={{ fontFamily: "'DM Serif Display', serif" }}
          >
            Smarter care,
            <br />
            <em className="not-italic text-[#FE7F2D]">seamless</em>
            <br />
            management.
          </h1>
          <p className="text-white/55 text-[15px] leading-[1.7] max-w-[300px] font-light">
            Your all-in-one hospital reception platform — appointments, patients,
            and records at your fingertips.
          </p>
        </div>

        {/* Stats */}
        <div className="flex gap-9 relative z-10">
          {[
            { num: "24/7",  label: "Emergency Service" },
            { num: "12K+",  label: "Trusted Patients" },
            { num: "50+",   label: "Qualified Doctors" },
          ].map((s, i) => (
            <React.Fragment key={s.label}>
              {i > 0 && <div className="w-px bg-white/15 self-stretch" />}
              <div className="flex flex-col gap-1">
                <span
                  className="text-white text-[26px]"
                  style={{ fontFamily: "'DM Serif Display', serif" }}
                >
                  {s.num}
                </span>
                <span className="text-[11px] text-white/40 uppercase tracking-[0.8px] font-medium">
                  {s.label}
                </span>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* ── Right form panel ── */}
      <div className="flex-1 flex items-center justify-center px-8 py-10 bg-white">
        <div className="w-full max-w-[420px] fade-up">

          <h2
            className="text-[#0a2540] text-[32px] mb-1"
            style={{ fontFamily: "'DM Serif Display', serif" }}
          >
            Welcome back
          </h2>
          <p className="text-[#8a9bb0] text-sm mb-10">
            Sign in to your receptionist portal
          </p>

          {/* Email field */}
          <FieldGroup label="Email Address">
            <FieldWrap focused={focused === "email"} icon={<MdEmail size={18} />}>
              <input
                className="w-full pl-11 pr-4 py-[14px] border-[1.5px] border-[#dde4ed] rounded-xl text-[15px] text-[#0a2540] bg-white outline-none transition-all placeholder:text-[#b0bcc9] focus:border-[#e96f1f] focus:shadow-[0_0_0_4px_rgba(30,165,108,0.1)]"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
                type="email"
                placeholder="you@samhospital.org"
                value={data.email}
                onChange={(e) => handleChange(e.target.value, "email")}
                onFocus={() => setFocused("email")}
                onBlur={() => setFocused("")}
                onKeyDown={handleKeyDown}
              />
            </FieldWrap>
          </FieldGroup>

          {/* Password field */}
          <FieldGroup label="Password">
            <FieldWrap focused={focused === "password"} icon={<MdLock size={18} />}>
              <input
                className="w-full pl-11 pr-11 py-[14px] border-[1.5px] border-[#dde4ed] rounded-xl text-[15px] text-[#0a2540] bg-white outline-none transition-all placeholder:text-[#b0bcc9] focus:border-[#e96f1f] focus:shadow-[0_0_0_4px_rgba(30,165,108,0.1)]"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                value={data.password}
                onChange={(e) => handleChange(e.target.value, "password")}
                onFocus={() => setFocused("password")}
                onBlur={() => setFocused("")}
                onKeyDown={handleKeyDown}
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8a9bb0] hover:text-[#1EA56C] transition-colors p-1 rounded-md"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
              >
                {showPassword ? <MdVisibilityOff size={18} /> : <MdVisibility size={18} />}
              </button>
            </FieldWrap>
          </FieldGroup>

          {/* Error message */}
          {error && (
            <p className="flex items-center gap-1.5 text-[13px] text-[#e05c5c] -mt-3 mb-4">
              <MdErrorOutline size={16} />
              {error}
            </p>
          )}

          {/* Submit button */}
          <button
            className="w-full py-[15px] rounded-xl text-white text-base font-semibold tracking-[0.3px] transition-all disabled:opacity-65 disabled:cursor-not-allowed hover:enabled:-translate-y-px"
            style={{
              background: "linear-gradient(135deg, #FE7F2D 40%, #e96f1f 100%)",
              boxShadow: "0 4px 20px #e96f1f",
              fontFamily: "'DM Sans', sans-serif",
            }}
            onClick={handleSubmit}
            disabled={loading}
          >
            <span className="flex items-center justify-center gap-2.5">
              {loading ? (
                <>
                  <AiOutlineLoading3Quarters size={18} className="animate-spin" />
                  Signing in…
                </>
              ) : (
                <>
                  Sign In
                  <MdArrowForward size={18} />
                </>
              )}
            </span>
          </button>

        </div>
      </div>
    </div>
  );
};

/* ── Helper components ── */

const FieldGroup = ({ label, children }) => (
  <div className="mb-5 relative">
    <label
      className="block text-[11px] font-semibold tracking-[0.8px] uppercase text-[#4a6080] mb-2"
      style={{ fontFamily: "'DM Sans', sans-serif" }}
    >
      {label}
    </label>
    {children}
  </div>
);

const FieldWrap = ({ focused, icon, children }) => (
  <div className="relative flex items-center">
    <span
      className={`absolute left-4 flex items-center pointer-events-none transition-colors ${
        focused ? "text-[#e96f1f]" : "text-[#8a9bb0]"
      }`}
    >
      {icon}
    </span>
    {children}
  </div>
);

export default Login;